import { PhotoProvider, PhotoView } from "react-photo-view";
import Select, { MultiValue } from "react-select";
import { Switch } from "react-animated-switch";
import { PiCarDuotone } from "react-icons/pi";
import { CiImageOff } from "react-icons/ci";
import styles from "./Parking.module.scss";
import { CiZoomIn } from "react-icons/ci";
import React from "react";

interface ParkingSlot {
  ParkingId: number;
  ParkingName: string;
}

export interface ParkingAmenity {
  parkingid?: number;
  parkingId?: number;
  amenityId?: number;
  name?: string;
  amenityName?: string;
  image?: string;
  rates?: number;
  cost?: number;
  parkingdata?: ParkingSlot[];
}

type SlotOption = {
  label: string;
  value: string;
};

interface Props {
  parkings: ParkingAmenity[];
  parkingIds: number[];
  setParkingIds: React.Dispatch<React.SetStateAction<number[]>>;
  notifyParkingIds: number[];
  setNotifyParkingIds: React.Dispatch<React.SetStateAction<number[]>>;
}

function Parking(props: Props) {
  const {
    parkings,
    parkingIds,
    setParkingIds,
    notifyParkingIds,
    setNotifyParkingIds,
  } = props;

  const getParkingId = (amenity: ParkingAmenity) =>
    amenity.parkingid ?? amenity.parkingId ?? amenity.amenityId ?? 0;

  const getAmenitySlotIds = (amenity: ParkingAmenity) =>
    (amenity.parkingdata ?? []).map((slot) => slot.ParkingId);

  const getSelectedSlotIds = (amenity: ParkingAmenity) => {
    const amenitySlotIds = getAmenitySlotIds(amenity);
    return parkingIds.filter((id) => amenitySlotIds.includes(id));
  };

  const handleSlotsChange = (
    amenity: ParkingAmenity,
    selected: MultiValue<SlotOption>,
  ) => {
    const amenitySlotIds = getAmenitySlotIds(amenity);
    const nextSelectedIds = selected.map((option) => Number(option.value));

    setParkingIds((prev) => [
      ...prev.filter((id) => !amenitySlotIds.includes(id)),
      ...nextSelectedIds,
    ]);
  };

  const toggleNotify = (parkingId: number, checked: boolean) => {
    setNotifyParkingIds((prev) =>
      checked
        ? prev.includes(parkingId)
          ? prev
          : [...prev, parkingId]
        : prev.filter((id) => id !== parkingId),
    );
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.headingContainer}>
        <p className={styles.heading}>Parking</p>
        <p className={styles.subHeading}>Select parking spaces</p>
      </div>

      {parkings?.map((opt, index) => {
        const parkingId = getParkingId(opt);
        const parkingSlots = opt.parkingdata ?? [];
        const noneAvailable = parkingSlots.length === 0;
        const parkingAvailable = !noneAvailable;
        const selectedSlotIds = getSelectedSlotIds(opt);
        const rate = opt.rates ?? opt.cost ?? 0;
        const price = rate * selectedSlotIds.length;
        const displayName = opt.name ?? opt.amenityName ?? "Parking";
        const hasSelection =
          selectedSlotIds.length > 0 || notifyParkingIds.includes(parkingId);

        const slotOptions: SlotOption[] = parkingSlots.map((slot) => ({
          label: slot.ParkingName,
          value: String(slot.ParkingId),
        }));

        const selectedOptions = slotOptions.filter((option) =>
          selectedSlotIds.includes(Number(option.value)),
        );

        return (
          <div
            key={index}
            className={`${styles.parkingGroupContainer} ${hasSelection ? styles.userAddedParking : ""}`}
          >
            <div className={styles.parkingGroup}>
              <div className={styles.iconContainer}>
                <PiCarDuotone
                  className={`${styles.icon} ${parkingAvailable ? styles.parkingAvailableIcon : ""}`}
                />
              </div>
              <div className={styles.parkingContentMainContainer}>
                <div className={styles.parkingContentContainer}>
                  <div className={styles.labelContainer}>
                    <p className={styles.label}>{displayName}</p>
                    {noneAvailable && (
                      <div className={styles.notAvailableContainer}>
                        <p className={styles.label}>Unavailable</p>
                      </div>
                    )}
                  </div>

                  <div className={styles.selectContainer}>
                    {!noneAvailable ? (
                      <Select
                        className={styles.select}
                        classNamePrefix="parkingSelect"
                        isMulti
                        options={slotOptions}
                        value={selectedOptions}
                        placeholder="Select parking slots"
                        onChange={(selected) =>
                          handleSlotsChange(opt, selected)
                        }
                        menuPosition="fixed"
                      />
                    ) : (
                      <div className={styles.noticeContainer}>
                        <div className={styles.switchContainer}>
                          <Switch
                            inActiveTrackColor="#DFDFDF"
                            activeTrackColor="#C0F496"
                            inActiveThumbColor="#000000"
                            activeThumbColor="#000000"
                            checked={notifyParkingIds.includes(parkingId)}
                            onChange={(val) => toggleNotify(parkingId, val)}
                          />
                        </div>
                        <p className={styles.label}>
                          Notify me when parking is available
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.priceContainer}>
                <p>${price}</p>
              </div>

              <div className={styles.imageContainer}>
                <PhotoProvider  >
                  <PhotoView key={opt.image} src={opt.image}>
                    <div className={styles.imageTrigger}>
                      {opt.image ? (
                        <img
                          src={opt.image}
                          className={styles.image}
                          alt={displayName}
                        />
                      ) : (
                        <CiImageOff className={styles.image} />
                      )}
                      <div className={styles.imageOverlay}>
                        <CiZoomIn className={styles.icon} />
                      </div>
                    </div>
                  </PhotoView>
                </PhotoProvider>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default Parking;
